import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Wallet, 
  Loader2,
  Search,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCommitmentsPage = () => {
  const { formatCurrency } = useI18n();
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });
  
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCommitments();
  }, [filters]);

  const fetchCommitments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      
      const response = await api.get('/admin/commitments', { params });
      let data = response.data.commitments || [];
      
      // Client-side search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        data = data.filter(c => 
          c.user_name?.toLowerCase().includes(search) ||
          c.user_email?.toLowerCase().includes(search) ||
          c.opportunity_title?.toLowerCase().includes(search)
        );
      }
      
      setCommitments(data);
      setTotal(response.data.total || data.length);
    } catch (error) {
      console.error('Error fetching commitments:', error);
    } finally {
      setLoading(false);
    }
  };

  const openStatusDialog = (commitment) => {
    setSelectedCommitment(commitment);
    setNewStatus(commitment.status);
    setStatusNotes('');
    setDialogOpen(true);
  };

  const updateStatus = async () => {
    if (!selectedCommitment) return;
    
    setUpdating(true);
    try {
      await api.put(`/admin/commitments/${selectedCommitment.commitment_id}/status`, {
        status: newStatus,
        notes: statusNotes,
      });
      toast.success('Estado actualizado');
      setDialogOpen(false);
      fetchCommitments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      countersign_pending: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Por Firmar', icon: Clock },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado', icon: CheckCircle2 },
      funds_pending: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Fondos Pendientes', icon: Clock },
      confirmed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Confirmado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      cancelled: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cancelado', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={style.className}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  // Calculate totals
  const totalsByStatus = commitments.reduce((acc, c) => {
    if (!acc[c.status]) acc[c.status] = { count: 0, amount: 0 };
    acc[c.status].count++;
    acc[c.status].amount += c.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="admin-commitments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Gestión de Compromisos</h1>
          <p className="body-text">{total} compromisos de inversión.</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Pendientes</p>
            <p className="text-2xl font-light">{totalsByStatus.pending?.count || 0}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(totalsByStatus.pending?.amount || 0)}</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Aprobados</p>
            <p className="text-2xl font-light">{totalsByStatus.approved?.count || 0}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(totalsByStatus.approved?.amount || 0)}</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Fondos Pendientes</p>
            <p className="text-2xl font-light">{totalsByStatus.funds_pending?.count || 0}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(totalsByStatus.funds_pending?.amount || 0)}</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard bg-emerald-500/5">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Confirmados</p>
            <p className="text-2xl font-light text-emerald-500">{totalsByStatus.confirmed?.count || 0}</p>
            <p className="text-xs text-emerald-500">{formatCurrency(totalsByStatus.confirmed?.amount || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por inversionista o proyecto..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="approved">Aprobado</SelectItem>
                <SelectItem value="funds_pending">Fondos Pendientes</SelectItem>
                <SelectItem value="confirmed">Confirmado</SelectItem>
                <SelectItem value="rejected">Rechazado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Commitments List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : commitments.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground">No hay compromisos</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {commitments.map((commitment) => (
            <Card key={commitment.commitment_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(commitment.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(commitment.created_at).toLocaleDateString('es-MX')}
                      </span>
                      {commitment.proof_of_transfer_doc_id && (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                          Comprobante
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium mb-1">{commitment.opportunity_title || 'Proyecto'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {commitment.user_name} ({commitment.user_email})
                    </p>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Monto</p>
                      <p className="text-xl font-light text-primary">
                        {formatCurrency(commitment.amount, commitment.currency)}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openStatusDialog(commitment)}
                      className="gap-2"
                    >
                      <Eye className="h-4 w-4" strokeWidth={1.5} />
                      Gestionar
                    </Button>
                  </div>
                </div>

                {commitment.status_notes && (
                  <div className="mt-4 p-3 bg-muted/30 rounded-sm">
                    <p className="text-xs text-muted-foreground">Nota: {commitment.status_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Status Update Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar Compromiso</DialogTitle>
          </DialogHeader>
          
          {selectedCommitment && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Inversionista</span>
                  <span>{selectedCommitment.user_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proyecto</span>
                  <span>{selectedCommitment.opportunity_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monto</span>
                  <span className="font-medium text-primary">
                    {formatCurrency(selectedCommitment.amount, selectedCommitment.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fuente de fondos</span>
                  <span className="capitalize">{selectedCommitment.investment_source?.replace('_', ' ') || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Comprobante</span>
                  <span>{selectedCommitment.proof_of_transfer_doc_id ? '✓ Subido' : '✗ Pendiente'}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nuevo Estado</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="approved">Aprobado</SelectItem>
                    <SelectItem value="funds_pending">Fondos Pendientes</SelectItem>
                    <SelectItem value="confirmed">Confirmado</SelectItem>
                    <SelectItem value="rejected">Rechazado</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notas (visibles para el inversionista)</Label>
                <Textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Notas sobre el cambio de estado..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={updateStatus} 
              disabled={updating}
              className="btn-primary"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Actualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommitmentsPage;
